import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {AiSupportedLanguageCodesEnum} from '../../../../../../../libs/shared/core/src/helpers/ai/constants';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  arrayToggle,
  insertOrMove,
} from '../../../../../../../libs/shared/core/src/helpers/utilities/arrayHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dropdown, DropdownProps} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownFooter} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownFooter';
import {DropdownHeader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {AiTranslateLanguageInput} from './aiTranslateLanguageInput';

/*
 * Props.
 */

export interface AiTranslateSettingsDialogProps {
  dropdownProps: DropdownProps;
  preferredLanguages: ReadonlyArray<AiSupportedLanguageCodesEnum>;
  onSave: (settings: ReadonlyArray<AiSupportedLanguageCodesEnum>) => void;
}

const StyledWrapper = styled.div`
  padding: 18px 20px;
`;

/*
 * Component.
 */

export const AiTranslateSettingsDropdown: FC<AiTranslateSettingsDialogProps> = ({
  dropdownProps,
  preferredLanguages,
  onSave,
}) => {
  const [neverTranslateLanguages, setNeverTranslateLanguages] = useState(preferredLanguages);
  const translateLanguage = neverTranslateLanguages[0];

  return (
    <Dropdown {...dropdownProps}>
      <DropdownHeader
        title={<Trans id="message-viewer-translate-settings-header-title">Translations settings</Trans>}
      />
      <StyledWrapper>
        <AiTranslateLanguageInput
          dropdownBoxProps={{
            label: (
              <Trans id="message-viewer-translate-settings-translate-language-label">
                Always translate to
              </Trans>
            ),
          }}
          headerTitle={<Trans id="message-viewer-translate-dropdown-header-title">Always translate to</Trans>}
          selectedLanguages={[translateLanguage]}
          onSelectLanguage={(code) => setNeverTranslateLanguages((current) => insertOrMove(current, 0, code))}
        />
        <AiTranslateLanguageInput
          dropdownBoxProps={{
            label: (
              <Trans id="message-viewer-translate-settings-translate-blacklist-label">Never translate</Trans>
            ),
          }}
          headerTitle={
            <Trans id="message-viewer-translate-settings-translate-blacklist-dropdown-header-title">
              Never translate
            </Trans>
          }
          isMultipleChoices
          disabledLanguages={[translateLanguage]}
          selectedLanguages={neverTranslateLanguages}
          onSelectLanguage={(code) => setNeverTranslateLanguages((current) => arrayToggle(current, code))}
        />
      </StyledWrapper>
      <DropdownFooter>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={() => dropdownProps.onRequestClose?.()}>
            <Trans id="message-viewer-translate-settings-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={() => onSave(neverTranslateLanguages)}>
            <Trans id="message-viewer-translate-settings-save">Save</Trans>
          </Button>
        </ButtonGroup>
      </DropdownFooter>
    </Dropdown>
  );
};
